import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';

import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../../constants/event-items';
import Alert from '../../../../../../../components/global/Alert';
import {
  ALBUM_SUBTYPE,
  ALBUM_UPLOAD_PERMISSIONS,
} from '../../../../../../../constants/albums';
import BoxedCustomControl from '../../../../../../../components/global/BoxedCustomControl';
import CustomControl from '../../../../../../../components/global/CustomControl';
import InfoCircleIcon from '../../../../../../../components/icons/InfoCircleIcon';
import { isAlbumFolder } from '../../../../../../../util/album-folder';
import Badge from '../../../../../../../components/global/Badge';
import { isSpecialtyAlbum } from '../../../../../../../util/album';
import {
  isNativeAppWebView,
  postNativeAppFreemiumEventTutorialConfigInStepPurchase,
} from '../../../../../../../services/native-bridge/native-apps-interface';
import { EVENT_ADDON_ITEM_UPSELL_MODAL } from '../../../../../../../components/global/EventAddonItemUpsell';
import { PURCHASED_EVENT_ADDON_FEATURES } from '../../../../../services/featurePermissions/helpers';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import { getEventAddonItemsByType } from '../../../../../components/FreemiumEventPurchase/helpers';

const propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  stepProps: PropTypes.object.isRequired,
};

const FreemiumEventTutorialPermissionsStep = ({
  values,
  errors,
  handleChange,
  handleBlur,
  stepProps,
}) => {
  const openModal = useOpenModal();
  const isNativeView = isNativeAppWebView();
  const errorList = Object.values(errors);
  const { eventAddonItems, purchasedAddonItems, entity } = stepProps;
  const eventType = entity?.album_subtype;

  const [waldofiedEventAddon] = getEventAddonItemsByType(
    eventAddonItems,
    EVENT_ADDON_ITEM_TYPE.WALDOFIED,
  );

  const isAlbumFolderEntity = isAlbumFolder(entity);

  const groupVideoPurchased = !!purchasedAddonItems.find(
    purchasedAddonItem =>
      purchasedAddonItem.event_addon_type === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
  );
  const memoryBookPurchased = !!purchasedAddonItems.find(
    purchasedAddonItem =>
      purchasedAddonItem.event_addon_type === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
  );

  const waldofiedPurchased = !!purchasedAddonItems.find(purchasedAddonItem =>
    [EVENT_ADDON_ITEM_TYPE.WALDOFIED].includes(
      purchasedAddonItem.event_addon_type,
    ),
  );

  const isLiveSlideshowPermissionsStep =
    purchasedAddonItems?.length === 1 && waldofiedPurchased;

  const title = (() => {
    if (!isAlbumFolderEntity && memoryBookPurchased) {
      return (
        <>
          Want to collab with others to build your slideshow?
          <br /> Waldo can help!{' '}
          <span
            className="label-tip"
            data-for="tooltip-event-create-permission-step"
            data-tip="Waldo's texting bot will make it easy to gather old photos from others who can simply text them back!"
            data-event="click"
            data-effect="solid"
          >
            <InfoCircleIcon size={20} />

            <ReactTooltip
              id="tooltip-event-create-permission-step"
              className="tooltip"
              globalEventOff="click"
            />
          </span>
        </>
      );
    }

    if (isLiveSlideshowPermissionsStep) {
      return "Do you want to approve other's photos prior to their being added to your Live Slideshow?";
    }

    if (!isAlbumFolderEntity && groupVideoPurchased) {
      return `May others add videos to the ${
        eventType === ALBUM_SUBTYPE.WEDDINGS ? 'Video Guestbook' : 'Group Video'
      }?`;
    }

    if (groupVideoPurchased) {
      return 'May others add photos and videos to the albums?';
    }

    return 'May others add photos to the albums?';
  })();

  const requiresReviewLabel = (() => {
    if (!isAlbumFolderEntity && groupVideoPurchased) {
      return 'I want to approve videos prior to being published';
    }

    if (groupVideoPurchased) {
      return 'I want to approve photos and videos prior to being published';
    }

    return 'I want to approve photos prior to being published';
  })();

  const canChangeRequireReview = () => {
    if (!isAlbumFolderEntity && isSpecialtyAlbum(entity)) {
      return true;
    }

    if (waldofiedPurchased) {
      return true;
    }

    return false;
  };

  return (
    <Fragment>
      <h4>{title}</h4>
      <BoxedCustomControl
        label="Yes"
        type="checkbox"
        id="permission-yes"
        name="permission"
        onChange={() => {
          if (isLiveSlideshowPermissionsStep) {
            handleChange({
              target: {
                name: 'require_review',
                value: true,
              },
            });
          } else {
            handleChange({
              target: {
                name: 'permission',
                value: ALBUM_UPLOAD_PERMISSIONS.ANYONE,
              },
            });
          }
        }}
        checked={
          isLiveSlideshowPermissionsStep
            ? values.require_review
            : values.permission === ALBUM_UPLOAD_PERMISSIONS.ANYONE
        }
        className="mb-2 text-center"
        boxStyle="floating"
      />
      <BoxedCustomControl
        label="No"
        type="checkbox"
        id="permission-no"
        name="permission"
        onChange={() => {
          if (isLiveSlideshowPermissionsStep) {
            handleChange({
              target: {
                name: 'require_review',
                value: false,
              },
            });
          } else {
            handleChange({
              target: {
                name: 'permission',
                value: ALBUM_UPLOAD_PERMISSIONS.NONE,
              },
            });
          }
        }}
        checked={
          isLiveSlideshowPermissionsStep
            ? !values.require_review
            : values.permission === ALBUM_UPLOAD_PERMISSIONS.NONE
        }
        className="mb-2 text-center"
        boxStyle="floating"
      />
      {values.permission === ALBUM_UPLOAD_PERMISSIONS.ANYONE &&
        !isLiveSlideshowPermissionsStep && (
          <div className="w-100 d-flex justify-content-between align-items-center">
            <CustomControl
              id="require_review"
              type="checkbox"
              name="require_review"
              label={requiresReviewLabel}
              checked={values.require_review}
              onChange={event => {
                if (canChangeRequireReview()) {
                  handleChange(event);
                } else if (isNativeView) {
                  // trigger event for native
                  postNativeAppFreemiumEventTutorialConfigInStepPurchase({
                    entityId: entity.id,
                    eventAddonItemId: waldofiedEventAddon?.id,
                    eventAddonItemType: EVENT_ADDON_ITEM_TYPE.WALDOFIED,
                    featureId: PURCHASED_EVENT_ADDON_FEATURES.require_review,
                  });
                } else {
                  openModal(EVENT_ADDON_ITEM_UPSELL_MODAL, {
                    albumSubtype: entity?.album_subtype,
                    albumFolderId: isAlbumFolderEntity
                      ? entity.id
                      : entity?.album_folder?.id,
                    albumId: !isAlbumFolderEntity ? entity.id : undefined,
                    eventAddonItemType: EVENT_ADDON_ITEM_TYPE.WALDOFIED,
                    featureId: PURCHASED_EVENT_ADDON_FEATURES.require_review,
                    onCheckoutSuccess: () => {
                      handleChange({
                        target: {
                          name: 'require_review',
                          value: !values.require_review,
                        },
                      });
                    },
                  });
                }
              }}
              onBlur={handleBlur}
              className="checkbox-require-photo-approval text-sm text-light align-items-center"
            />
            {!waldofiedPurchased &&
              (isAlbumFolderEntity || !isSpecialtyAlbum(entity)) && (
                <Badge
                  badgeSize="sm"
                  badgeStyle="gold"
                  className="text-white text-extra-bold"
                >
                  GOLD
                </Badge>
              )}
          </div>
        )}
      {errorList.length > 0 && (
        <Alert type="danger" className="mt-1 text-normal">
          <ul className="mb-0">
            {errorList.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Fragment>
  );
};

FreemiumEventTutorialPermissionsStep.propTypes = propTypes;

export default FreemiumEventTutorialPermissionsStep;
